// ECONOMY · TREASURE (v2-the-deep.md §9, §18.1, R2700): the body of the
// TREASURE pill. The pill stands only while the flag `treasure` holds
// (`FLAG_SUBS`). It has three sections, one 44 px build row each:
//
// - YOUR CACHES: the own player caches of `Empire.caches`, with their
//   finds and their note, and NOTE on a cache with no note.
// - CHART FRAGMENTS: the fragments of `Empire.chart_fragments`, each with
//   the cell of the pirate cache it points to and the mark of a cache a
//   hull of this commander took. The section stands only while `pirates`
//   holds, because no fragment exists without it.
// - FOUND: the caches a hull of this commander took this season
//   (`Empire.cache_finds`), with READ where the depositor left a note. The
//   section stands folded: its head counts the finds, and SHOW opens it.
// - WRECKS WITH CARGO (v2-the-deep.md §9.6, R2834, R2843): the wrecks of
//   the wreck window that this commander holds a cargo sighting of,
//   nearest to the own hold first. Line 1 is the class of the sunk hull
//   and the take of a SEARCH with the icons of a build price; line 2 is
//   the cell, the distance in cells and the age of the sighting. SEARCH,
//   the bright press, opens the SEARCH picker at the wreck point
//   (`ui/caches.ts`, R2860); VIEW, the dark press, centres the map on it.
//
// Every row with a place carries VIEW, which centres the map on the cell.
// NOTE and READ open the cache sheet (`ui/caches.ts`). The page is pure:
// it reads the state and writes markup.

import type { CacheRow, CargoSighting, ChartFragment, Wreck } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import { type GameState, costText } from '../../state';
import { esc } from '../esc';
import { buttonHtml } from '../cards/card';
import { markSvg, type MarkKind } from '../cards/marks';
import { cellLabel } from '../inbox/words';
import { seenText } from '../cards/treasure';
import { hexDistance } from '../../render/hex-ring';
import { cellAt, mpxToPx } from '../../world';
import { CACHE_FIND_OPEN, CACHE_NOTE_OPEN, cacheKindTitle, ownCaches, ownFinds } from '../caches';
import { buildRowHtml } from './buildrow';

/** The act of the fold of FOUND. */
export const TREASURE_FOUND = 'treasurefound';

/** The act of SEARCH on a row of WRECKS WITH CARGO (the wreck, the id of
 *  its sunk hull, R2860). */
export const TREASURE_SEARCH = 'treasuresearch';

/** What the WRECKS section reads of the state. */
type WreckState = Pick<GameState, 'wrecks' | 'cargoSightings' | 'rules' | 'seasonElapsedSec' | 'ship'
  | 'empire' | 'hello'>;

/** One row of WRECKS WITH CARGO: the wreck, its sighting, its cell and its
 *  distance in cells to the own hold (Infinity with no hold). */
interface KnownWreck {
  wreck: Wreck;
  sighting: CargoSighting;
  cell: number;
  cells: number;
}

/**
 * The wrecks that this commander knows a SEARCH can take cargo from (v2 §9.6,
 * R2843): the wrecks of the wreck window with a cargo sighting that is
 * not recovered (R2850), nearest
 * to the own hold first, a tie by the fleet id. A commander has one hold
 * (CONTEXT.md "hold"), so the nearest own hold is the home cell of the
 * Empire frame, or of the hello before the first Empire frame. A store
 * with no window cuts no wreck.
 */
export function knownWrecks(s: Omit<WreckState, 'ship'>): KnownWreck[] {
  if (s.cargoSightings.size === 0) return [];
  const span = s.rules.wreckWindowSec;
  const cut = span > 0 ? s.seasonElapsedSec() - span : -Infinity;
  const hold = s.empire?.homeCell ?? s.hello?.homeCell ?? -1;
  const out: KnownWreck[] = [];
  for (const w of s.wrecks) {
    const sighting = s.cargoSightings.get(w.fleet);
    // A recovered row is no cargo (R2850).
    if (!sighting || sighting.recovered || w.sunkSec < cut) continue;
    const cell = cellAt(mpxToPx(w.xMpx), mpxToPx(w.yMpx));
    out.push({ wreck: w, sighting, cell, cells: hexDistance(hold, cell) });
  }
  return out.sort((a, b) => (a.cells === b.cells ? 0 : a.cells < b.cells ? -1 : 1) || a.wreck.fleet - b.wreck.fleet);
}

/** The chart fragments of this commander with their places, in the order
 *  of the wire. */
function ownFragments(s: Pick<GameState, 'empire'>): readonly ChartFragment[] {
  return s.empire?.chartFragments ?? [];
}

/** A mark of the card kit in the icon slot of a build row. */
function rowMark(kind: MarkKind): string {
  return markSvg(kind, 'currentColor').replace('class="ck-mark"', 'class="bic"');
}

/** VIEW: the press of a row with a place. */
function viewPress(cell: number): string {
  return `<button class="bp ready" data-act="focuscell" data-n="${cell}" title="${esc(S.trs_view_title)}">`
    + `<span class="w">${esc(S.trs_view)}</span></button>`;
}

/** A quiet press in the figure column: NOTE or READ. */
function sidePress(act: string, id: number, label: string, title: string): string {
  return `<span class="cost">${buttonHtml({ act, id, face: 'quiet', label, title }, { keycaps: false })}</span>`;
}

/** One row of YOUR CACHES. */
function cacheRow(c: CacheRow): string {
  const note = c.note ? CS.cache_note_quoted({ note: c.note }) : CS.cache_note_unset;
  return buildRowHtml({
    icon: rowMark('cache'),
    name: `<span class="name">${esc(cellLabel(c.cell))}</span>`,
    line2: `${esc(CS.cache_finds({ n: c.finds }))} · ${esc(note)}`,
    side: c.noteSet ? '' : sidePress(CACHE_NOTE_OPEN, c.id, CS.cache_note_btn, CS.cache_note_btn_title),
    press: viewPress(c.cell),
  });
}

/** One row of CHART FRAGMENTS. */
function fragmentRow(f: ChartFragment): string {
  const line = S.trs_fragment_points({ cell: cellLabel(f.cell) });
  return buildRowHtml({
    icon: rowMark('pirate'),
    name: `<span class="name">${esc(S.trs_fragment_name({ den: f.den }))}</span>`,
    line2: esc(f.taken ? `${line} · ${S.trs_fragment_taken}` : line),
    press: viewPress(f.cell),
    cls: f.taken ? 'taken' : '',
  });
}

/** One row of FOUND. */
function findRow(c: CacheRow): string {
  return buildRowHtml({
    icon: rowMark('cache'),
    name: `<span class="name">${esc(c.label || cacheKindTitle(c.kind))}</span>`,
    line2: esc(cellLabel(c.cell)),
    side: c.note ? sidePress(CACHE_FIND_OPEN, c.id, CS.cache_read_btn, CS.cache_read_btn_title) : '',
    press: viewPress(c.cell),
  });
}

/**
 * One row of WRECKS WITH CARGO (R2843): the class and the take of a
 * SEARCH on line 1, the place, the distance and the age of the sighting
 * on line 2, and the pair SEARCH and VIEW in the presses of the SHOW and
 * RALLY pair of the siege window: SEARCH bright, VIEW dark (R2860).
 */
function wreckRow(s: WreckState, k: KnownWreck): string {
  const { wreck: w, cell } = k;
  const place = [cellLabel(cell)];
  if (Number.isFinite(k.cells)) place.push(S.trs_wreck_cells({ n: k.cells }));
  place.push(seenText(s, k.sighting));
  const acts = '<span class="trs-acts">'
    + `<button class="ck-b" data-act="${TREASURE_SEARCH}" data-id="${w.fleet}" title="${esc(S.trs_search_title)}">`
    + `<span>${esc(S.trs_search)}</span></button>`
    + `<button class="ck-b dk" data-act="focuscell" data-n="${cell}" title="${esc(S.trs_view_title)}">`
    + `<span>${esc(S.trs_view)}</span></button></span>`;
  return buildRowHtml({
    // Every row is a sighted wreck, so it wears the cargo bale (R2847).
    icon: rowMark('wreckcargo'),
    name: `<span class="name">${esc(s.ship(w.cls)?.name ?? '')}</span>`
      + ` <span class="take">${costText(k.sighting.take)}</span>`,
    line2: esc(place.join(' · ')),
    press: acts,
    cls: 'wreck',
  });
}

/** The page of ECONOMY · TREASURE. `foundOpen` unfolds FOUND. */
export function treasureHtml(s: Pick<GameState, 'empire' | 'hasFeatureFlag'> & WreckState, foundOpen: boolean): string {
  const caches = ownCaches(s);
  let html = `<h3>${S.trs_head_caches({ n: caches.length })}</h3>`
    + (caches.length > 0 ? caches.map(cacheRow).join('') : `<div class="empty">${S.trs_caches_empty}</div>`);
  if (s.hasFeatureFlag('pirates')) {
    const frags = ownFragments(s);
    html += `<h3>${S.trs_head_fragments({ n: frags.length })}</h3>`
      + (frags.length > 0 ? frags.map(fragmentRow).join('') : `<div class="empty">${S.trs_fragments_empty}</div>`);
  }
  const finds = ownFinds(s);
  html += `<h3>${S.trs_head_found({ n: finds.length })}</h3>`;
  if (finds.length === 0) {
    html += `<div class="empty">${S.trs_found_empty}</div>`;
  } else {
    if (foundOpen) html += finds.map(findRow).join('');
    html += `<div class="acts"><button data-act="${TREASURE_FOUND}" aria-expanded="${foundOpen}">`
      + `${foundOpen ? S.trs_found_hide : S.trs_found_show({ n: finds.length })}</button></div>`;
  }
  const wrecks = knownWrecks(s);
  html += `<h3>${S.trs_head_wrecks({ n: wrecks.length })}</h3>`
    + (wrecks.length > 0 ? wrecks.map((k) => wreckRow(s, k)).join('') : `<div class="empty">${S.trs_wrecks_empty}</div>`);
  return html + `<div class="empty">${S.trs_hint}</div>`;
}
